import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Box,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
} from '@mui/material';
import { Campaign as CampaignIcon, Send, Schedule } from '@mui/icons-material';
import { createCampaign, getCampaigns } from '../redux/slices/campaignSlice';
import { getWhatsAppAccounts } from '../redux/slices/whatsappSlice';

export default function Campaigns() {
    const dispatch = useDispatch();
    const { campaigns, loading, error } = useSelector((state) => state.campaign);
    const whatsappAccounts = useSelector((state) => state.whatsapp.accounts || []);

    const [formData, setFormData] = useState({
        name: '',
        type: 'whatsapp',
        accountId: '',
        targetGroup: '',
        messageText: '',
        isScheduled: false,
        sendAt: '',
        delayMin: 20,
        delayMax: 90,
    });

    useEffect(() => {
        dispatch(getCampaigns());
        dispatch(getWhatsAppAccounts());
    }, [dispatch]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.type === 'whatsapp' && !formData.accountId) {
            alert('Please select a WhatsApp account');
            return;
        }

        const targetArray = formData.targetGroup.split('\n').filter(t => t.trim());

        const campaignData = {
            name: formData.name,
            type: formData.type,
            accountId: formData.accountId,
            targetGroup: targetArray,
            message: { text: formData.messageText },
            schedule: {
                isScheduled: formData.isScheduled,
                sendAt: formData.isScheduled ? new Date(formData.sendAt) : null,
            },
            delay: {
                min: formData.delayMin,
                max: formData.delayMax,
            },
        };

        dispatch(createCampaign(campaignData));
        setFormData({
            name: '',
            type: 'whatsapp',
            accountId: '',
            targetGroup: '',
            messageText: '',
            isScheduled: false,
            sendAt: '',
            delayMin: 20,
            delayMax: 90,
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'running': return 'primary';
            case 'failed': return 'error';
            default: return 'default';
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                <CampaignIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: { xs: 32, md: 40 } }} />
                Campaigns
            </Typography>

            <Grid container spacing={3}>
                {/* Create Campaign */}
                <Grid item xs={12} md={5}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Create New Campaign</Typography>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Campaign Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value, accountId: '' })}
                                        label="Type"
                                    >
                                        <MenuItem value="whatsapp">WhatsApp</MenuItem>
                                        <MenuItem value="facebook-retargeting">Facebook Retargeting</MenuItem>
                                    </Select>
                                </FormControl>

                                {formData.type === 'whatsapp' && (
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>WhatsApp Account</InputLabel>
                                        <Select
                                            value={formData.accountId}
                                            onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                            label="WhatsApp Account"
                                            required
                                        >
                                            {whatsappAccounts.filter(acc => acc.status === 'connected').map((account) => (
                                                <MenuItem key={account._id} value={account._id}>
                                                    {account.number || account.phoneNumber || account._id}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Target Numbers/IDs (one per line)"
                                    value={formData.targetGroup}
                                    onChange={(e) => setFormData({ ...formData, targetGroup: e.target.value })}
                                    placeholder="966501234567&#10;966509876543"
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Message"
                                    value={formData.messageText}
                                    onChange={(e) => setFormData({ ...formData, messageText: e.target.value })}
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Schedule</InputLabel>
                                    <Select
                                        value={formData.isScheduled}
                                        onChange={(e) => setFormData({ ...formData, isScheduled: e.target.value })}
                                        label="Schedule"
                                    >
                                        <MenuItem value={false}>Send Now</MenuItem>
                                        <MenuItem value={true}>Schedule</MenuItem>
                                    </Select>
                                </FormControl>

                                {formData.isScheduled && (
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Send At"
                                        value={formData.sendAt}
                                        onChange={(e) => setFormData({ ...formData, sendAt: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mb: 2 }}
                                    />
                                )}

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Min Delay (sec)"
                                            value={formData.delayMin}
                                            onChange={(e) => setFormData({ ...formData, delayMin: parseInt(e.target.value) })}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Max Delay (sec)"
                                            value={formData.delayMax}
                                            onChange={(e) => setFormData({ ...formData, delayMax: parseInt(e.target.value) })}
                                        />
                                    </Grid>
                                </Grid>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    startIcon={formData.isScheduled ? <Schedule /> : <Send />}
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : (formData.isScheduled ? 'Schedule Campaign' : 'Send Campaign')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Campaign List */}
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Campaign History</Typography>

                            {campaigns.length === 0 ? (
                                <Typography color="text.secondary">No campaigns yet</Typography>
                            ) : (
                                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Targets</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Created</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {campaigns.map((campaign) => (
                                                <TableRow key={campaign._id}>
                                                    <TableCell>{campaign.name}</TableCell>
                                                    <TableCell>
                                                        <Chip label={campaign.type} size="small" />
                                                    </TableCell>
                                                    <TableCell>{campaign.targetGroup.length}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={campaign.status}
                                                            color={getStatusColor(campaign.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(campaign.createdAt).toLocaleDateString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}
